import { createContext, useContext } from 'react';

/**
 * Portal host context + hooks (theme-scope-aware overlays).
 *
 * Modal overlays — {@link ActionSheet}, {@link Dialog}, the hero onboarding — render through a React
 * portal so they escape any ancestor stacking / opacity / transform context (a locked card dimmed to
 * `opacity-70` must not bleed onto a fixed overlay). The naive target is `document.body`, but the body
 * sits OUTSIDE every theme container: the persona shell scopes its bespoke CSS vars (`--ink`,
 * `--surface`, `--muted`, …) on its `.kid` root, and the kid persona pins its `--color-*` token mode to
 * light — neither of which a `document.body` portal inherits, so an overlay there falls back to the OS
 * colour scheme (dark sheet, empty `--ink`, dark-on-dark text).
 *
 * The persona shell therefore mounts an in-scope host element (a child of its themed root) and provides
 * it through {@link PortalHostContext}; overlays portal into it via {@link usePortalTarget} and thus
 * inherit the exact same computed theme as the page behind them. The value is `null` wherever no shell
 * provides a host (unit tests, the login screen) — there {@link usePortalTarget} falls back to
 * `document.body`, preserving the previous behaviour.
 *
 * (Context + hooks live in this `.ts` file, mirroring `toast-context.ts`, so no React fast-refresh
 * boundary is crossed — the shell consumes `PortalHostContext.Provider` directly.)
 */
export const PortalHostContext = createContext<HTMLElement | null>(null);

/** The provided in-scope host, or `null` when no shell is mounted (tests / login). */
export function usePortalHost(): HTMLElement | null {
  return useContext(PortalHostContext);
}

/** The concrete portal target: the in-scope host when a shell provides one, else `document.body`. */
export function usePortalTarget(): HTMLElement {
  return useContext(PortalHostContext) ?? document.body;
}
